/**
 * # FlowPage
 * > a custom lifecycle for our pages
 * 
 * like simple flow but handle more stuff for page
 * - loading + app ready etc
 * - @TODO SEO metas
 * 
 */
import { tryOnMounted } from '@vueuse/core';
import { store as cursorStore } from 'components/UiKit/Cursor/store';
import { client, server, withMotion } from 'config';
import { keys, map, merge, zipObject } from 'lodash';
import { ComputedRef, Ref, computed, nextTick, readonly, ref, unref, watch } from 'vue';

/**
* STATES are incrmental values
* *onLoaded will be triggered even if current state higher like READY*
* (destroy states may be negative so super specific)
*/
export const STATES = {
  DEFAULT:0,
  LOADED:1,
  INIT:2,
  INIT_UI:3,
  READY:4
}


export interface FlowOptions<T extends Obj> {
  data?:()=>Ref<T>
  api?:{
    url: string,
    moc?: ()=>Promise<Obj>
  }
  /** load your data
  * will wait this loader, store data, then setState(LOADED)
  * > HOOK: ON_INIT avaible aftre this complete
  */
  load?: ()=>Promise<Obj>
  /** preload ui dependencies + wait tryOnMounted
    * triggered only if `motion`- start loading on setup but do not bloack maiin loading state
    * > HOOK: ON_INIT_UI avaible after completion
    * ui:{
    *   gsap: ()=>import('gsap').then(m=>m.default)
    * }
  **/
  ui?: {
    [key:string]:()=>Promise<any>
  }
  /** if true, log flow etc */
  debug?:boolean|string
}

export type FlowHookArgs = {
  client:boolean, server:boolean, motion:boolean
}
// type FlowCallback = (arg0:{
//   client:boolean, server:boolean, motion:boolean
// })=>unknown

export interface Flow<T> {
  /** 
  * ready to render CPN
  * - actualy event before STATES.READY (when ui hidden)
  * - true when INIT/INIT_UI
  */
  ready:ComputedRef<boolean>
  readyToRender:ComputedRef<boolean>
  //  loaded:ComputedRef<boolean>
  state:Ref<number>
  /** return api data - if response = {meta, data} then getData will return only the `data` node (without seo) */
  getData:()=>Ref<T>
  /**
  * data loaded - like a tryOnMounted()
  * - if server = right after loaded
  * - if client = loaded + mounted
  */
  onInit: (next: (arg: FlowHookArgs & {data:T}) => unknown) => Promise<unknown>
  /**
  * init ui stuff (init + preload ui + moUnted)
  * **only if motion**
  */
  onInitUi: (next: (arg: FlowHookArgs & {data:T} & {ui:Obj}) => unknown) => Promise<unknown>
}


export function useFlow<T extends Obj>(options:FlowOptions<T>={}):Flow<T>{
  const state = ref<number>(STATES.DEFAULT)
  let uiVendors = {}
  const motion = withMotion()
  
  const _data = ref(options.data?.() ?? {} as T) // store the response `meta` node (like no meta or data.data)
  function getData(){
    // const ensured:unknown = readonly(_data)
    return readonly(_data)
  }

  /**
  * to be triggered once loading ready
  * May wait some required states before
  */
  function setReady(){
    if(state.value === STATES.READY) return
    const requiredState = motion ? STATES.INIT_UI : STATES.INIT
    waitForState(requiredState)
    .then(()=>{
      state.value = STATES.READY
    })
  }
  
  function waitForState(stateToMatch:number){
    return new Promise(resolve=>{
      const unwatch = watch(state, (current)=>{
        if (current>=stateToMatch){
          nextTick(()=>unwatch())
          resolve(true)
        }
      }, {immediate:true, flush:'post'})
    })
  }

  async function load(){
    return new Promise(resolve=>{
      if (typeof options.load === 'function')
        return resolve(options.load())
      return resolve({})
    })
    .then((response?:any)=>{
      function mergeWithOptionsData(data:any){
        if (!options.data) return data
        return merge({}, options.data(), data??{})
      }
      if(typeof response === 'object' && response){
        _data.value = mergeWithOptionsData(response)
      }
      state.value = STATES.LOADED
      return response
    })
  }
  
  async function preloadUi(){
    return new Promise(resolve=>{
      if (options.ui) {
        return Promise.all(map(options.ui, i=>i()))
        .then((args)=>zipObject(keys(options.ui), args))
        .then(resolve)
      }
      return resolve(undefined)
    })
    .then(payload=>{
      if(payload && typeof payload === 'object'){ 
        uiVendors = payload // no reactive shits here
      }
      return payload
    })
  }
  

  Promise.all([
    load(),
    new Promise(resolve=>{
      if(server) return resolve(true)
      tryOnMounted(()=>resolve(true))
    })
  ])
  .then((data)=>{
    nextTick(()=>{ state.value = STATES.INIT })
    return data
  })

  if (motion) {
    Promise.all([
      preloadUi(),
      waitForState(STATES.INIT),
      new Promise(resolve=>tryOnMounted(()=>resolve(true)))
    ]).then((ui)=>{
      nextTick(()=>{ state.value = STATES.INIT_UI })
      return ui
    })
  }
  
  setReady()

  return {
    loaded:computed(()=>state.value >= STATES.LOADED),
    ready:computed(()=>state.value >= STATES.READY),
    readyToRender:computed(()=>state.value >= (motion?STATES.INIT_UI:STATES.INIT)),
    getData,
    setReady,
    waitForState,
    state,
    /** data loaded - like a tryOnMounted() */
    onInit:async function(next:(arg:FlowHookArgs & {data:T})=>unknown){
      await waitForState(STATES.INIT)
      return next({client, server, motion, data:(unref(_data) as T)})
    },
    onInitUi:async function(next:(arg:FlowHookArgs & {data:T} & {ui:Obj})=>unknown){
      if (!motion) return
      await waitForState(STATES.INIT_UI)

      // update cursor
      if (client) {
        cursorStore.updateCursor();
      }

      return next({client, server, motion, data:(unref(_data) as T), ui:uiVendors})
    },

  } as Flow<T>
}

export default useFlow

